import { useState } from "react";
import { toast } from "sonner";
import { Mic, Square, SendHorizonal, Loader2, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useRecorder } from "@/hooks/useRecorder";
import { api, apiError } from "@/lib/api";

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function Composer({ chat }) {
  const [text, setText] = useState("");
  const [transcribing, setTranscribing] = useState(false);

  const onAudio = async (blob, ext) => {
    if (blob.size < 2000) { rec.setError("Enregistrement trop court. Maintenez l'enregistrement au moins une seconde."); return; }
    setTranscribing(true);
    const fd = new FormData();
    fd.append("file", blob, `audio.${ext}`);
    try {
      const { data } = await api.post("/ai/transcribe", fd);
      if (!data.text) rec.setError("Je n'ai rien entendu de clair. Réessayez ou écrivez votre demande.");
      else { setText((t) => (t ? `${t} ${data.text}` : data.text)); toast("Transcription prête : vérifiez le texte puis envoyez."); }
    } catch (e) {
      rec.setError(apiError(e));
    } finally { setTranscribing(false); }
  };
  const rec = useRecorder(onAudio);

  const submit = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || chat.sending || chat.loading) return;
    setText("");
    if (!(await chat.send(t))) setText(t);
  };

  return (
    <form onSubmit={submit} className="border-t bg-white p-3" data-testid="assistant-composer">
      {chat.error && <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="assistant-error">{chat.error}</div>}
      {rec.error && <div className="mb-2 flex items-start gap-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="assistant-mic-error"><MicOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />{rec.error}</div>}
      {rec.recording && (
        <div className="mb-2 flex items-center gap-2 rounded bg-rose-600 px-3 py-2 text-sm font-semibold text-white" data-testid="assistant-recording-indicator">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-white" /> Enregistrement… <span className="font-mono" data-testid="assistant-recording-time">{mmss(rec.seconds)}</span>
          <span className="ml-auto text-xs font-normal opacity-80">max 01:00</span>
        </div>
      )}
      {transcribing && <div className="mb-2 flex items-center gap-2 text-xs text-slate-500" data-testid="assistant-transcribing"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Transcription en cours…</div>}
      <div className="flex items-end gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Ex. : sors 2 kits 001, combien de 006 ?…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          className="min-h-[52px] flex-1 resize-none text-sm" data-testid="assistant-input" />
        <Button type="button" variant={rec.recording ? "destructive" : "outline"} className="h-[52px] w-[52px] p-0" disabled={transcribing}
          onClick={rec.recording ? rec.stop : rec.start} title={rec.recording ? "Arrêter l'enregistrement" : "Dicter"} data-testid="assistant-mic-button">
          {rec.recording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </Button>
        <Button type="submit" className="h-[52px] w-[52px] p-0" disabled={!text.trim() || chat.sending || chat.loading} data-testid="assistant-send-button">
          {chat.sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizonal className="h-5 w-5" />}
        </Button>
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">Entrée pour envoyer · l'IA propose, vous validez. Rien n'est modifié sans « Valider ».</p>
    </form>
  );
}
